/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import classNames from 'classnames';
import { Container } from 'src/components/layouts/Container/Container';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigationItems } from '../../hooks/useNavigationItems';
import styles from '../styles/TrackingAuthForm.module.scss';
import { SearchModels } from '../../components/ui/TableOverview/utils/SearchFieldRegistry';
import { DataView } from '../../components/ui/TableOverview/DataView';
import { useDataViewController } from '../../hooks/useDataViewController';
import { MappingListItem, useConnectorProductMappingsQuery } from '../../services/connector';
import Overlay from '../../components/ui/Overlay/Overlay';
import OverlayHead from '../../components/ui/Overlay/OverlayHead';
import { IntegrationsMappingOverlay } from './IntegrationsMappingOverlay';
import Icon from '../../components/ui/Icon/Icon';
import Image from '../../components/ui/Image/Image';

function IntegrationsMappings() {
  const { t } = useTranslation();
  const [selectedMapping, setSelectedMapping] = useState<MappingListItem | null>(null);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('integrations/mappings');

  const columnHelper = createColumnHelper<MappingListItem>();

  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      size: 80,
      enableHiding: false,
      meta: { label: t('general.id') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row?.name, {
      id: 'name',
      meta: { label: t('integrations.name') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.category, {
      id: 'category',
      meta: { label: t('general.category') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.sku, {
      id: 'sku',
      meta: { label: t('general.sku') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.barcode, {
      id: 'barcode',
      meta: { label: t('general.barcode') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const value = info.getValue();
        const isEmpty = value == null || value === '' || value.length === 0;

        return <span className={classNames(styles.cellTitle, { [styles.code]: !isEmpty })}>{value}</span>;
      },
    }),
    columnHelper.accessor(row => row.parent, {
      id: 'parent',
      size: 80,
      meta: { label: t('general.parent') },
      enableSorting: false,
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.linked_product, {
      id: 'linked_product',
      size: 80,
      meta: { label: t('general.linked_product') },
      enableSorting: false,
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.connector_logo_url, {
      id: 'connector_name',
      size: 80,
      meta: { label: t('general.connector') },
      enableSorting: false,
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex align-middle justify-center">
          <Image
            className="w-[28px] h-[28px]"
            imageUrl={info.getValue() || ''}
            alt="integration"
            fallbackComponent={<Icon name="package" width={28} height={28} />}
          />
        </div>
      ),
    }),
  ];
  const controller = useDataViewController();

  return (
    <Container
      title={`${t('integrations.integrations')} / ${t('integrations.mappings')}`}
      className={classNames(styles.root, 'relative py-32 flex-1')}>
      <DataView<MappingListItem>
        applyContainer={false}
        showFilters={false}
        showTabs={false}
        allowBulkActions={accessRights?.read_write}
        filterModelId="df.mapping.product"
        kpiModel="df.mapping.product"
        headline={t('integrations.mappings')}
        modelKey={SearchModels.Mappings}
        controller={controller}
        columns={columns}
        useDataQuery={useConnectorProductMappingsQuery}
        actions={[
          {
            title: t('general.edit'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              setSelectedMapping(selected[0] ?? null);
            },
            showInContextMenu: true,
          },
        ]}
        onRowClick={(row: MappingListItem) => setSelectedMapping(row ?? null)}
      />
      {selectedMapping && selectedMapping.id && (
        <Overlay>
          <OverlayHead
            title={selectedMapping.external_id ?? undefined}
            onCloseOverlay={() => setSelectedMapping(null)}
          />
          <IntegrationsMappingOverlay mappingId={selectedMapping.id} onClose={() => setSelectedMapping(null)} />
        </Overlay>
      )}
    </Container>
  );
}

export default IntegrationsMappings;
